"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState } from "react";

const createFormSchema = (errors: any) => z.object({
  name: z.string().min(2, { message: errors.name }),
  email: z.string().email({ message: errors.email }),
  message: z.string().min(10, { message: errors.message }),
});

type FormValues = {
  name: string;
  email: string;
  message: string;
};

interface ContactFormProps {
  content: any;
}

export function ContactForm({ content }: ContactFormProps) {
  const { title, subtitle, fields, submitButton, success, errors } = content;
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(createFormSchema(errors)),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(errors.general);
      }

      // GTM Event
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({ event: "contact_form_submit" });
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Submission error:", error);
      alert(errors.general);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-[32px] shadow-2xl p-10 w-full max-w-2xl mx-auto border border-zinc-100 text-center space-y-4">
        <div className="h-16 w-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        </div>
        <h2 className="text-3xl font-bold text-zinc-900">{success.title}</h2>
        <p className="text-zinc-500 text-lg">{success.subtitle}</p>
        <button 
          onClick={() => setIsSubmitted(false)}
          className="mt-8 text-blue-600 font-bold hover:underline"
        >
          Enviar outra mensagem
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-4">{title}</h1>
        <p className="text-lg text-zinc-500 max-w-md">
          {subtitle}
        </p>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Nome */}
          <div className="space-y-3">
            <label htmlFor="name" className="text-sm font-semibold text-zinc-700 px-1">
              {fields.name.label}
            </label>
            <input
              id="name"
              {...form.register("name")}
              placeholder={fields.name.placeholder}
              className="w-full px-5 py-4 rounded-2xl border-none bg-zinc-100 focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all text-zinc-900 placeholder:text-zinc-400 font-medium outline-none"
            />
            {form.formState.errors.name && (
              <p className="text-xs text-red-500 font-bold px-1">{form.formState.errors.name.message}</p>
            )}
          </div>

          {/* E-mail */}
          <div className="space-y-3">
            <label htmlFor="email" className="text-sm font-semibold text-zinc-700 px-1">
              {fields.email.label}
            </label>
            <input
              id="email"
              type="email"
              {...form.register("email")}
              placeholder={fields.email.placeholder}
              className="w-full px-5 py-4 rounded-2xl border-none bg-zinc-100 focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all text-zinc-900 placeholder:text-zinc-400 font-medium outline-none"
            />
            {form.formState.errors.email && (
              <p className="text-xs text-red-500 font-bold px-1">{form.formState.errors.email.message}</p>
            )}
          </div>
        </div>

        {/* Mensagem */}
        <div className="space-y-3">
          <label htmlFor="message" className="text-sm font-semibold text-zinc-700 px-1">
            {fields.message.label}
          </label>
          <textarea
            id="message"
            {...form.register("message")}
            placeholder={fields.message.placeholder}
            rows={5}
            className="w-full px-5 py-4 rounded-2xl border-none bg-zinc-100 focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all text-zinc-900 placeholder:text-zinc-400 font-medium outline-none resize-none"
          />
          {form.formState.errors.message && (
            <p className="text-xs text-red-500 font-bold px-1">{form.formState.errors.message.message}</p>
          )}
        </div>

        {/* Botão de Envio */}
        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-5 rounded-2xl shadow-lg transition-all active:scale-[0.98] disabled:opacity-70 disabled:active:scale-100 text-lg uppercase tracking-wider"
        >
          {form.formState.isSubmitting ? "..." : submitButton}
        </button>
      </form>
    </div>
  );
}
